import React, {useState, useEffect} from "react";
import Header from "../../components/Private/Header";
import {ReactComponent as Arrow} from "../../assets/icons/right.svg";
import {useParams, useSearchParams} from "react-router";
import {useAppNavigate} from "../../lib/use-app-navigate";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {request} from "../../lib/request";
import {Spinner} from "@heroui/react";
import {getErrorMessage, toastError, toastSuccess} from "../../lib/toast";

function normalizeDiscountExpiryPreset(preset) {
    if (preset === "end_of_today" || preset === "end_of_tomorrow" || preset === "custom") {
        return "end_of_today";
    }

    return preset || "none";
}

const PrivateProductsMenuEdit = () => {
    const navigate = useAppNavigate();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const filterQuery = searchParams.toString();
    const listFilters = {
        category_id: searchParams.get("category_id") || undefined,
        search: searchParams.get("search") || undefined,
    };
    const [idItem, setIdItem] = useState(id);
    const [productData, setProductData] = useState(null);
    const [overallDiscount, setOverallDiscount] = useState("");
    const [discountExpiryPreset, setDiscountExpiryPreset] = useState("none");
    const [saveFeedback, setSaveFeedback] = useState(null);
    const queryClient = useQueryClient();

    useEffect(() => {
        setIdItem(id);
    }, [id]);

    const {data, isLoading} = useQuery({
        queryKey: ["products", "show", idItem, listFilters.category_id, listFilters.search],
        queryFn: async () =>
            await request({
                method: "get",
                url: `/restaurant/products/show/${idItem}`,
                query: listFilters,
            }),
        enabled: !!idItem,
        keepPreviousData: true,
    });

    useEffect(() => {
        if (data?.data) {
            // Initialize local state with fetched data
            const initialData = {
                ...data.data,
                options: data.data.options.map(option => ({
                    ...option,
                    price: parseFloat(option.price) || 0,
                    price_discount: parseFloat(option.price_discount) || parseFloat(option.price) || 0,
                    discount_percentage: parseFloat(option.discount_percentage) || 0,
                })),
            };
            setProductData(initialData);

            // Check if all options have the same discount_percentage
            const discounts = initialData.options.map(opt => opt.discount_percentage);
            const allSame = discounts.every(d => d === discounts[0]);
            setOverallDiscount(allSame && discounts[0] > 0 ? String(discounts[0]) : "");
            setDiscountExpiryPreset(
                normalizeDiscountExpiryPreset(initialData.discount_expiry_preset)
            );
        }
    }, [data]);

    const handleInputChange = (e, field) => {
        setProductData(prev => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    const handleOverallDiscountChange = (e) => {
        const raw = e.target.value;
        setOverallDiscount(raw);

        // Empty overall discount must clear every option discount (not leave stale %).
        const disc = raw === "" ? 0 : (parseFloat(raw) || 0);

        setProductData(prev => ({
            ...prev,
            options: prev.options.map(opt => ({
                ...opt,
                discount_percentage: disc,
                price_discount: Math.round(Number(opt.price || 0) * (1 - disc / 100)),
            })),
        }));
    };

    const handleOptionChange = (e, optionId, field) => {
        const value = field === 'name' ? e.target.value : parseFloat(e.target.value) || 0;

        setProductData(prev => ({
            ...prev,
            options: prev.options.map(opt => {
                if (opt.id === optionId) {
                    const updatedOpt = {...opt};

                    if (field === 'name') {
                        updatedOpt.name = value;
                    } else if (field === 'price_discount') {
                        updatedOpt.price_discount = value;
                        updatedOpt.price = value;
                        updatedOpt.discount_percentage = 0;
                    } else if (field === 'discount_percentage') {
                        updatedOpt.discount_percentage = value;
                        updatedOpt.price_discount = Math.round(updatedOpt.price * (1 - value / 100));
                    }

                    return updatedOpt;
                }
                return opt;
            }),
        }));
    };

    const {mutate, isPending} = useMutation({
        mutationFn: async (updatedData) => {
            return await request({
                method: "put",
                url: `/restaurant/products/update/${idItem}`,
                data: updatedData,
            });
        },
        onSuccess: (res) => {
            const message = res.message || "تغییرات با موفقیت ذخیره شد";
            setSaveFeedback({type: "success", message});
            toastSuccess(message);
            window.scrollTo({top: 0, behavior: "smooth"});
            queryClient.invalidateQueries({queryKey: ["products", "show", idItem]});
            queryClient.invalidateQueries({queryKey: ["products", "index_food"]});
        },
        onError: (error) => {
            const message = getErrorMessage(error);
            setSaveFeedback({type: "error", message});
            toastError(message);
            window.scrollTo({top: 0, behavior: "smooth"});
            console.error(error);
        }
    });

    const handleSave = () => {
        if (!productData) return;
        if (!productData.panel_editable) {
            const message = 'دسترسی ویرایش غذا بسته شده است.';
            setSaveFeedback({type: "error", message});
            toastError(message);
            window.scrollTo({top: 0, behavior: "smooth"});
            return;
        }

        if (!String(productData.name || "").trim()) {
            const message = "اسم غذا باید وارد شود";
            setSaveFeedback({type: "error", message});
            toastError(message);
            window.scrollTo({top: 0, behavior: "smooth"});
            return;
        }

        // Only foods with real variants need option titles; single empty-named options are OK.
        if (productData.options.length > 1) {
            const missingName = productData.options.find((option) => !String(option.name || "").trim());
            if (missingName) {
                const message = "نام را وارد کنید";
                setSaveFeedback({type: "error", message});
                toastError(message);
                window.scrollTo({top: 0, behavior: "smooth"});
                return;
            }
        }

        setSaveFeedback(null);
        mutate({
            id: productData.id,
            name: productData.name,
            description: productData.description,
            discount_expiry_preset: discountExpiryPreset,
            options: productData.options.map((option) => ({
                id: option.id,
                name: productData.options.length > 1 ? String(option.name || "").trim() : String(option.name || ""),
                price: option.price,
                price_discount: option.price_discount,
                discount_percentage: option.discount_percentage,
            })),
        });
    };

    if (isLoading || !productData) {
        return <Spinner/>;
    }

    const d = productData;

    return (
        <>
            <Header title={<Arrow className="rotate-180 cursor-pointer" onClick={() => navigate("/products-menu")}/>}/>
            {saveFeedback && (
                <div
                    className={`rest-menu-save-feedback rest-menu-save-feedback--${saveFeedback.type}`}
                    role="alert"
                >
                    {saveFeedback.message}
                </div>
            )}
            <div className="flex items-center justify-center w-full flex-col gap-6">
                <div
                    className="relative flex flex-col items-center p-3 lg:p-6 max-w-[1000px] w-full border-3 border-gray-100 rounded-xl z-1">
                    <div
                        style={{backgroundImage: `url('/images/logo1.png')`}}
                        className="absolute !w-80 !h-72 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-no-repeat bg-center bg-cover z-0 opacity-60"
                    ></div>
                    <div
                        className="relative z-10 flex items-center justify-center py-3 w-full shadow-lg rounded-xl text-lg lg:text-xl text-[#177E39] font-bold">
                        عنوان
                    </div>
                    <div className="relative z-10 flex flex-col w-full">
                        <div
                            className="flex items-center gap-4 w-full border-b-2 border-b-gray-100 px-6 lg:px-10 py-6">
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-bold">نوع غذا</p>
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-medium">{d.category}</p>
                        </div>
                        <div
                            className="flex items-center justify-between w-full border-b-2 border-b-gray-100 gap-4 px-6 lg:px-10 py-6">
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-bold whitespace-nowrap">عنوان
                                اصلی</p>
                            <input
                                type="text"
                                value={d.name || ""}
                                onChange={(e) => handleInputChange(e, "name")}
                                className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-1 w-full"
                            />
                        </div>
                        <div
                            className="flex items-center justify-between w-full gap-4 border-b-2 border-b-gray-100 px-6 lg:px-10 py-6">
                            <p className="text-sm lg:text-base whitespace-nowrap text-[#4F4F4F] font-bold">توضیحات</p>
                            <textarea
                                rows={8}
                                value={d.description || ""}
                                onChange={(e) => handleInputChange(e, "description")}
                                className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-1 w-full"
                            />
                        </div>
                        {d?.options?.length > 1 && <div
                            className="flex items-center justify-between w-full gap-4 border-b-2 border-b-gray-100 px-6 lg:px-10 py-6">
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-bold whitespace-nowrap">میزان تخفیف
                                کلی (%)</p>
                            <input
                                type="number"
                                value={overallDiscount}
                                onChange={handleOverallDiscountChange}
                                placeholder="—"
                                className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-1 w-full"
                            />
                        </div>}
                        <div className="flex flex-col w-full gap-2 border-b-2 border-b-gray-100 px-6 lg:px-10 py-6">
                            <div className="flex items-center justify-between w-full gap-4">
                                <p className="text-sm lg:text-base text-[#4F4F4F] font-bold whitespace-nowrap">مدت تخفیف</p>
                                <select
                                    value={discountExpiryPreset}
                                    onChange={(e) => setDiscountExpiryPreset(e.target.value)}
                                    className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-2 w-full"
                                >
                                    <option value="none">بدون محدودیت زمانی</option>
                                    <option value="end_of_today">تا پایان امروز (لحظه‌های داغ)</option>
                                </select>
                            </div>
                            {discountExpiryPreset === "end_of_today" ? (
                                <p className="text-xs lg:text-sm text-[#707070]">
                                    تخفیف لحظه‌های داغ تا پایان امروز اعمال می‌شود و نیمه‌شب به‌صورت خودکار ریست می‌شود.
                                </p>
                            ) : (
                                <p className="text-xs lg:text-sm text-[#707070] leading-6">
                                    در صورت اعمال تخفیف بدون محدودیت زمانی حتما در بخش تخفیفات درخواست پیشنهاد طعم دار ثبت کنید تا توضیحات تخفیف در صفحه اصلی سایت قرار گیرد
                                </p>
                            )}
                        </div>
                    </div>
                    {d?.options?.length > 1 && <div
                        className="relative z-10 flex items-center justify-center py-3 w-full text-center shadow-lg rounded-xl text-lg lg:text-xl text-[#177E39] font-bold bg-white">
                        زیرمجموعه‌
                    </div>}
                    <div className="relative z-10 flex flex-col w-full">
                        <div
                            className={`hidden md:grid ${d?.options?.length > 1 ? "grid-cols-4" : "grid-cols-3"} gap-2 px-3 lg:px-10 py-4 w-full text-center border-b-2 border-b-gray-100 bg-gray-50 rounded-t-lg`}>
                            {d?.options?.length > 1 &&
                                <p className="text-sm lg:text-base text-[#4F4F4F] font-bold">عنوان زیرمجموعه</p>}
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-bold">قیمت قدیم</p>
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-bold">قیمت</p>
                            <p className="text-sm lg:text-base text-[#4F4F4F] font-bold">میزان تخفیف(%)</p>
                        </div>
                        {d?.options?.map((i) => (
                            <div key={i.id}
                                 className={`flex flex-col md:grid ${d?.options?.length > 1 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3 md:gap-2 px-3 lg:px-10 py-4 w-full border-b-2 border-b-gray-100 bg-white/80`}>
                                {d?.options?.length > 1 && (
                                    <label className="flex flex-col gap-1 text-right md:text-center">
                                        <span className="md:hidden text-xs text-[#707070] font-bold">عنوان زیرمجموعه</span>
                                        <input
                                            type="text"
                                            value={i.name || ""}
                                            onChange={(e) => handleOptionChange(e, i.id, "name")}
                                            className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-2 w-full"
                                        />
                                    </label>
                                )}
                                <label className="flex flex-col gap-1 text-right md:text-center md:justify-center">
                                    <span className="md:hidden text-xs text-[#707070] font-bold">قیمت قدیم</span>
                                    <p className="text-sm lg:text-base text-[#4F4F4F] font-medium py-2">
                                        {Number(i.price || 0).toLocaleString()} تومان
                                    </p>
                                </label>
                                <label className="flex flex-col gap-1 text-right md:text-center">
                                    <span className="md:hidden text-xs text-[#707070] font-bold">قیمت</span>
                                    <input
                                        type="number"
                                        value={i.price_discount || ""}
                                        onChange={(e) => handleOptionChange(e, i.id, "price_discount")}
                                        className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-2 w-full"
                                    />
                                </label>
                                <label className="flex flex-col gap-1 text-right md:text-center">
                                    <span className="md:hidden text-xs text-[#707070] font-bold">میزان تخفیف (%)</span>
                                    <input
                                        type="number"
                                        value={i.discount_percentage || ""}
                                        onChange={(e) => handleOptionChange(e, i.id, "discount_percentage")}
                                        className="text-sm lg:text-base text-[#4F4F4F] font-medium border border-gray-300 rounded p-2 w-full"
                                    />
                                </label>
                            </div>
                        ))}
                    </div>
                    <div
                        onClick={handleSave}
                        className={`max-w-52 w-full text-center py-2 bg-[#CBFFA3] text-[#177E39] border border-[#177E39] rounded-lg mt-6 cursor-pointer ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                        {isPending ? <Spinner size="small"/> : "ذخیره"}
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-8 max-w-[1000px] w-full">
                    <div
                        onClick={() => {
                            if (d?.previous_id) {
                                navigate(`/products-menu/${d.previous_id}${filterQuery ? `?${filterQuery}` : ''}`);
                            }
                        }}
                        className={`bg-white text-center  font-bold w-full ${!!d?.previous_id ? "text-Primary-950 border-Primary-950" : "text-Primary-400 border-Primary-400"} rounded-lg py-2 border cursor-pointer`}
                    >
                        قبلی
                    </div>

                    <div
                        onClick={() => {
                            if (d?.next_id) {
                                navigate(`/products-menu/${d.next_id}${filterQuery ? `?${filterQuery}` : ''}`);
                            }
                        }}
                        className={`${!!d?.next_id ? "bg-Primary-950 border-Primary-950" : "bg-Primary-400 border-Primary-400"} text-center text-white font-bold w-full rounded-lg py-2 border  cursor-pointer`}
                    >
                        بعدی
                    </div>
                </div>
            </div>
        </>
    );
};

export default PrivateProductsMenuEdit;